import { PrimaryButton } from '@/components/Buttons/Primary/primaryButton';
import { Product } from '@/context/types';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export const Card = ({
  product,
  selectedQty,
  onAdd,
  onRemove,
  disabled,
  hideReadMore = false,
}: {
  product: Product;
  selectedQty: number;
  onAdd: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onRemove: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  hideReadMore?: boolean;
}) => {
  return (
    // 'w-full' allows the parent <div className="grid grid-cols-2 gap-x-3 gap-y-12"> to perfectly size 2 cards on mobile.
    // pt-12 (padding-top) gives the grid row enough space so the overlapping image doesn't hit the item above it.
    <div className="w-full flex flex-col group pt-12 sm:pt-16 !font-display">
      <div className="relative flex-1 flex flex-col justify-end bg-white rounded-2xl sm:rounded-[var(--radius-artisan)] pb-4 sm:pb-6 px-2 sm:px-4 shadow-sm hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.15)] transition-shadow duration-300">
        {/* --- OUT-OF-BOUNDS IMAGE CONTAINER --- */}
        {/* Negative top margin pulls the image outside the white card. 
            w-[115%] makes it slightly wider than the card itself for maximum "pop" */}
        <div className="relative w-[115%] sm:w-[110%] mx-auto aspect-square -mt-16 sm:-mt-20 z-10 flex items-center justify-center pointer-events-none">
          {product?.img?.url ? (
            <motion.img
              src={product?.img?.url}
              alt={product?.img?.altText ?? undefined}
              className="w-[85%] h-[85%] sm:w-full sm:h-full object-contain drop-shadow-[0_15px_15px_rgba(0,0,0,0.35)] will-change-transform pointer-events-auto"
              // Slight hover scale enhances the 3D effect without breaking the layout
              whileHover={{ y: -6, scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            />
          ) : (
            <div className="w-[80%] h-[80%] bg-brand-cream/50 rounded-full flex items-center justify-center drop-shadow-md border border-brand-truffle/10 pointer-events-auto">
              <span className="px-2 text-text-muted/40 font-display text-[10px] sm:text-xs tracking-widest uppercase text-center">
                {product.productType}
              </span>
            </div>
          )}

          {/* Disabled Overlay specifically for the image */}
          {disabled && selectedQty === 0 && (
            <div className="absolute inset-0 bg-white/40 z-20 rounded-full transition-opacity duration-300 pointer-events-none" />
          )}
        </div>

        {/* --- TYPOGRAPHY --- */}
        {/* Responsive text sizes ensure it fits nicely on a 375px mobile screen */}
        <div
          // 1. Added `w-full` here to give the flex container a strict horizontal boundary
          className="relative flex flex-col items-center justify-center gap-1 sm:gap-2 mt-4 sm:mt-4 mb-4 sm:mb-5 h-10 sm:h-12 w-full"
          title={product.title}
        >
          {(product as any)?.capacity && (
            <div
              // 2. left-full places it exactly on the right edge of the text, no matter how short the word is.
              // 3. -top-2 lifts it slightly like a superscript.
              className="absolute -top-2 -right-[20px] sm:-right-[25px] h-6 bg-brand-error flex items-center justify-center text-[11px] text-white z-30 font-[500] font-display pl-4 pr-3 shadow-md whitespace-nowrap [clip-path:polygon(10px_0,100%_0,100%_100%,10px_100%,0_50%)] rounded-md"
            >
              <span className="translate-y-[1px]">{`${(product as any)?.capacity} ct.`}</span>
            </div>
          )}
          {/* 2. Removed `truncate` to let `line-clamp-2` do its job properly */}
          {/* 3. Added `w-full` so the span respects the parent's width limit */}
          <span className="relative text-text-heading text-center !text-md !sm:text-lg font-[500] !font-display line-clamp-2 tracking-wide leading-snug w-full px-1">
            {product.title}
          </span>
        </div>

        {/* --- FLUID CONTROLS CONTAINER --- */}
        {/* Fixed height ensures button morphing doesn't cause layout shift */}
        <div className="h-8 sm:h-10 flex items-center justify-center w-full relative z-20">
          <AnimatePresence mode="wait">
            {selectedQty === 0 ? (
              <motion.div
                key="add-button"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="absolute w-[70%] sm:w-[85%] flex justify-center"
              >
                <PrimaryButton
                  onClick={onAdd}
                  disabled={disabled}
                  className="bg-brand-truffle border-2 border-brand-truffle hover:bg-brand-cream focus:bg-brand-cream focus:text-brand-truffle hover:text-brand-truffle !font-display text-brand-cream !font-[400] text-[12px] sm:text-[16px] uppercase tracking-widest leading-none !min-w-0 w-full h-8 sm:h-10 !px-0 !py-0 !rounded-full flex items-center justify-center transition-colors shadow-sm disabled:opacity-50 disabled:hover:bg-brand-truffle disabled:hover:text-brand-cream"
                  title={'Add'}
                />
              </motion.div>
            ) : (
              <motion.div
                key="qty-controls"
                initial={{ opacity: 0, scale: 0.95, width: '100px' }}
                animate={{ opacity: 1, scale: 1, width: '110px' }} // Wider on mobile
                exit={{ opacity: 0, scale: 0.95, width: '100px' }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="absolute flex items-center justify-between bg-brand-truffle border-2 border-brand-truffle rounded-full px-1.5 sm:px-2 h-8 sm:h-10 shadow-md sm:w-[130px]!"
              >
                {/* Decrement Button */}
                <button
                  onClick={onRemove}
                  className="w-6 h-6 sm:w-7 sm:h-7 p-0 flex items-center justify-center rounded-full text-brand-cream hover:bg-white/20 active:scale-90 transition-all"
                  aria-label="Decrease quantity"
                >
                  <svg
                    className="h-3 w-3 sm:h-4 sm:w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M20 12H4"
                    />
                  </svg>
                </button>

                {/* Quantity Number (Animated) */}
                <motion.span
                  key={selectedQty}
                  initial={{ y: -8, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="text-center text-brand-cream font-display !font-[700] text-[11px] sm:text-xs leading-none"
                >
                  {selectedQty}
                </motion.span>

                {/* Increment Button */}
                <button
                  onClick={onAdd}
                  disabled={disabled}
                  className="w-6 h-6 sm:w-7 sm:h-7 p-0 flex items-center justify-center rounded-full text-brand-cream hover:bg-white/20 active:scale-90 transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:active:scale-100"
                  aria-label="Increase quantity"
                >
                  <svg
                    className="h-3 w-3 sm:h-4 sm:w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* --- READ MORE LINK --- */}
        {!hideReadMore && (
          <Link
            to={`/product/${product.numericId}`}
            className="underline underline-offset-[3px] text-brand-sage/80 hover:text-brand-sage !font-display !font-[400] transition-colors !text-[8px] !sm:text-[10px] uppercase tracking-wider text-center cursor-pointer mt-3 sm:mt-4"
          >
            Read More
          </Link>
        )}
      </div>
    </div>
  );
};
